import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl, type Segment } from '../../platform/primitives/SegmentedControl'
import { useToast } from '../../platform/feedback/ToastProvider'
import { appliedMessage } from './applyCopy'
import { ApplyNowDialog } from './ApplyNowDialog'
import { DeleteRuleDialog } from './DeleteRuleDialog'
import { RuleApplied } from './RuleApplied'
import { PAUSED_NOTE, RuleMatches } from './RuleMatches'
import { RuleRemoved } from './RuleRemoved'
import { useRuleFlow } from './RuleFlow'
import { readOnlyNote } from './RuleRow'
import { RULES_PATH } from './rulePaths'
import { WhenSentence } from './RuleSentence'
import { actionText, type Names } from './ruleText'
import type { ReadRule } from './ruleWire'
import type { Applied } from './useRuleApply'
import { useRuleExclusions } from './useRuleApply'
import { useRuleEverApplied, useRuleLogs } from './useRuleLogs'
import { useRuleMatches } from './useRuleMatches'
import './rules.css'
import './rulePage.css'

export const RULE_GONE = 'This rule no longer exists. It may have been deleted on another device.'

const EDIT_REASON_ID = 'rule-page-edit-reason'

/** The page's tabs: what the rule has changed, what it matches today, and what was taken off it. */
type RuleTab = 'applied' | 'matches' | 'removed'

/** The one rule out of the list, so the page and the list share a cache entry and an edit refreshes both. */
function ruleOf(list: AsyncState<ReadRule[]>, id: string | undefined): AsyncState<ReadRule> {
  if (list.status === 'loading' || list.status === 'error') return list
  const found = list.status === 'empty' ? undefined : list.data.find((r) => r.rule.id === id)
  return found === undefined ? { status: 'error', message: RULE_GONE } : { status: 'success', data: found }
}

/** "Oct 2, 2026": the day an instant fell on where the reader is. */
const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** "Last applied" when nothing is in effect: never run, or every run undone. */
const noneInEffect = (ever: boolean | undefined) => (ever === undefined ? '—' : ever ? 'All changes undone' : 'Not yet')

function Stats({ read, ruleId }: { read: ReadRule; ruleId: string }) {
  const { state } = useRuleLogs(ruleId)
  const total = state.status === 'success' ? state.data.total : state.status === 'empty' ? 0 : undefined
  const newest = state.status === 'success' ? state.data.logs[0] : undefined
  const ever = useRuleEverApplied(ruleId, total === 0)
  return (
    <dl className="rule-page__stats">
      <div className="rule-page__stat">
        {/* From the log of changes not undone, never `times_applied`, which an Undo does not lower. */}
        <dt className="rule-page__stat-label">Changes in effect</dt>
        <dd className="rule-page__stat-value">{total ?? '—'}</dd>
      </div>
      <div className="rule-page__stat">
        <dt className="rule-page__stat-label">Last applied</dt>
        <dd className="rule-page__stat-value">
          {newest !== undefined ? DAY.format(new Date(newest.appliedAt)) : total === 0 ? noneInEffect(ever) : '—'}
        </dd>
      </div>
      <div className="rule-page__stat">
        <dt className="rule-page__stat-label">Status</dt>
        <dd className="rule-page__stat-value">{read.rule.is_enabled ? 'Active' : 'Paused'}</dd>
      </div>
    </dl>
  )
}

function Sentence({ read, names }: { read: ReadRule; names: Names }) {
  if (!read.editable) return <p className="rule-page__sentence">{readOnlyNote(read)}</p>
  return (
    <div className="rule-page__rule">
      <p className="rule-page__sentence">
        <span className="rule-page__lead">When </span>
        <WhenSentence draft={read.draft} names={names} />
      </p>
      <div className="rule-page__actions">
        <span className="rule-page__lead">Then</span>
        {read.draft.actions.map((a, i) => (
          <span key={`${a.type}-${i}`} className="rule-row__action">
            {actionText(a, names)}
          </span>
        ))}
      </div>
    </div>
  )
}

const countOf = <T,>(state: AsyncState<T>, n: (d: T) => number): number | undefined =>
  state.status === 'success' ? n(state.data) : state.status === 'empty' ? 0 : undefined

function Body({
  read,
  names,
  onEdit,
  onUndo,
  onTurnOn,
}: {
  read: ReadRule
  names: Names
  onEdit: () => void
  /** Raise the Undo confirmation for an apply's batch. */
  onUndo: (batchId: string) => void
  onTurnOn: () => void
}) {
  const ruleId = read.rule.id
  const enabled = read.rule.is_enabled
  const [tab, setTab] = useState<RuleTab>('applied')
  const [applying, setApplying] = useState(false)
  const toast = useToast()
  const logs = useRuleLogs(ruleId)
  const matches = useRuleMatches(ruleId)
  const removed = useRuleExclusions(ruleId)
  const tabs: Segment<RuleTab>[] = [
    { value: 'applied', label: 'Applied to', count: countOf(logs.state, (d) => d.total) },
    // `/matches` has no total: the count is known once the last page is in, and not before.
    { value: 'matches', label: 'Matches now', count: matches.hasMore ? undefined : countOf(matches.state, (d) => d.matches.length) },
    { value: 'removed', label: 'Removed', count: countOf(removed.state, (d) => d.total) },
  ]
  // One toast per run, each with its own Undo. The dialog closes itself once a run is not cut short.
  const reportApplied = (result: Applied) => {
    toast.show({
      kind: 'success',
      message: appliedMessage(read.rule.name, result),
      ...(result.modified > 0 ? { action: { label: 'Undo', onAct: () => onUndo(result.batchId) } } : {}),
    })
  }
  return (
    <>
      <div className="rule-page__tools">
        {/* A paused rule is not offered Apply now (the server refuses it): it says to turn it on. */}
        {enabled ? (
          <Button variant="primary" size="page" onClick={() => setApplying(true)}>
            Apply now…
          </Button>
        ) : (
          <>
            <p className="rule-page__paused-note">{PAUSED_NOTE}</p>
            <Button variant="outline" size="page" onClick={onTurnOn}>
              Turn on
            </Button>
          </>
        )}
        {read.editable ? (
          <Button variant="outline" size="page" onClick={onEdit}>
            Edit rule
          </Button>
        ) : (
          <DisabledReason reason={readOnlyNote(read)} reasonId={EDIT_REASON_ID}>
            <Button variant="outline" size="page" aria-disabled="true" aria-describedby={EDIT_REASON_ID}>
              Edit rule
            </Button>
          </DisabledReason>
        )}
      </div>
      <Panel className="rule-page__summary">
        <Sentence read={read} names={names} />
        <Stats read={read} ruleId={ruleId} />
      </Panel>
      <SegmentedControl label="Rule view" name="rule-tab" options={tabs} value={tab} onChange={setTab} />
      <Panel className="rule-page__panel">
        {tab === 'applied' && <RuleApplied ruleId={ruleId} />}
        {tab === 'matches' && <RuleMatches read={read} onUndo={onUndo} />}
        {tab === 'removed' && <RuleRemoved ruleId={ruleId} />}
      </Panel>
      {applying && (
        <ApplyNowDialog ruleId={ruleId} ruleName={read.rule.name} onClose={() => setApplying(false)} onApplied={reportApplied} />
      )}
    </>
  )
}

/**
 * `/rules/:id`: one rule, read. The builder stays where it was, reached by Edit rule; a read-only rule
 * still gets the page, with Edit rule `aria-disabled` and its reason beside it.
 *
 * The rule comes out of the rules list (`useRuleFlow`'s `useRules`), not a second by-id read: the list
 * is already the cache every rule write invalidates, so an edit made here lands on both at once.
 */
export function RulePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [deleting, setDeleting] = useState<ReadRule | null>(null)
  const flow = useRuleFlow({ onDelete: setDeleting, restoreFocusTo: headingRef })
  const state = ruleOf(flow.state, id)
  const recovery =
    flow.state.status === 'error'
      ? flow.state.recovery
      : { label: 'Back to Auto-rules', onAct: () => void navigate(RULES_PATH) }

  return (
    <>
      <div className="rule-page__back">
        <InlineLink to={RULES_PATH}>← Back to Auto-rules</InlineLink>
      </div>
      <Async
        surface="rules"
        state={state.status === 'error' && recovery !== undefined ? { ...state, recovery } : state}
        skeleton={
          <>
            <Skeleton height={32} width={240} />
            <Skeleton height={96} />
            <Skeleton height={44} />
          </>
        }
        empty={{ title: RULE_GONE }}
      >
        {(read) => (
          <>
            <div className="rule-page__head">
              <h2 className="rule-page__title" ref={headingRef} tabIndex={-1}>
                {read.rule.name}
              </h2>
              {!read.rule.is_enabled && <span className="rule-row__paused">Paused</span>}
            </div>
            <Body
              read={read}
              names={flow.names}
              onEdit={() => flow.open(read)}
              onUndo={flow.saver.offerUndo}
              onTurnOn={() => void flow.saver.enable(read)}
            />
          </>
        )}
      </Async>
      {flow.element}
      {deleting !== null && (
        <DeleteRuleDialog
          read={deleting}
          names={flow.names}
          restoreFocusTo={headingRef}
          onClose={() => setDeleting(null)}
          onDone={(outcome) => {
            flow.afterDelete()
            setDeleting(null)
            if (outcome === 'deleted') void navigate(RULES_PATH)
          }}
        />
      )}
    </>
  )
}
